/**
 * AFS Legal OS — Article Generator Dashboard
 *
 * Roadmap ref: not itself a numbered roadmap item — Section 7 (7a–7d) built
 * every piece of the Article Generator's engine but never a screen counsel
 * can actually press a button on, unlike every other engine (5a/5b gave the
 * Contract Engine one, 6d gave the Research Assistant one). This closes
 * that gap using only what 7a–7d already built, the same "just routing, no
 * new logic" convention as 3d/7c — no new AI calls are added here that
 * don't already exist in articleResearch.ts / queryGenerator.ts /
 * articleDraftGenerator.ts.
 *
 * REDESIGN — query-only, manual paste-back (combined build plan, Part 1,
 * Phase 3). Swaps the old single-call `runResearchTurn()` loop for the same
 * generateNextQuery() → submitFindings() → generateFinalReport() sequence
 * ResearchDashboard (6d) now uses, so the article flow gets the identical
 * paste-your-own-research loop.
 *
 * FLOW — four stages, one screen:
 *   1. "New Topic"     → startArticleResearch() (7c): selects a topic (7b)
 *                        and creates a fresh, empty ResearchSession (6c's
 *                        factory). No AI research call yet — counsel sees
 *                        the topic before spending any calls on it.
 *   2. Research         → generateNextQuery() (6c) produces one query at a
 *                        time; counsel pastes back what they found and
 *                        submitFindings() (6c) synthesizes it — same
 *                        manual loop as ResearchDashboard (6d), never
 *                        auto-loops.
 *   3. "Generate Report" → generateFinalReport() (6c), enabled once there's
 *                        at least one completed turn — produces the
 *                        OSCOLA-footnoted report the draft is built from.
 *   4. "Generate Draft"  → generateArticleDraft() (7d), enabled once
 *                        `session.final_report` exists.
 *
 * NOT PERSISTED — same reasoning as ResearchDashboard (6d): 6b never added
 * session storage, and 7d never added draft storage. Everything here is
 * plain React state; refreshing the page loses the in-progress topic,
 * research, report, and draft. The one piece that IS persisted is the topic
 * log itself (7a's D1 table), written by selectNextTopic() inside
 * startArticleResearch() — that happens regardless of this screen.
 */

import { useState } from 'react';
import { useAppStore } from '@/state/appStore';
import { T, S } from '@/constants/tokens';
import { startArticleResearch } from '@/articles/articleResearch';
import { generateNextQuery, submitFindings, generateFinalReport, MAX_RESEARCH_TURNS } from '@/research/queryGenerator';
import { generateArticleDraft } from '@/articles/articleDraftGenerator';
import type { ResearchSession } from '@/research/types';
import type { ArticleTopicLogEntry } from '@/articles/types';
import type { ArticleDraft } from '@/articles/articleDraftGenerator';

function StatusPill({ status }: { status: ResearchSession['status'] }) {
  const copy: Record<ResearchSession['status'], { label: string; color: string }> = {
    active:    { label: 'Researching', color: T.info },
    exhausted: { label: 'Exhausted',   color: T.ok },
    abandoned: { label: 'Stopped',     color: T.mute },
  };
  const { label, color } = copy[status];
  return (
    <span style={{
      fontSize: 10, color, border: `1px solid ${color}`, borderRadius: 3,
      padding: '2px 8px', fontFamily: "'Times New Roman', Times, serif",
      letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700,
    }}>
      {label}
    </span>
  );
}

function CompletedEntryRow({ index, entry }: { index: number; entry: ResearchSession['entries'][number] }) {
  return (
    <div style={{
      border: `1px solid ${T.bdrL}`, borderRadius: 5, padding: '12px 14px',
      marginBottom: 10, background: '#ffffff',
    }}>
      <p style={{ ...S.h3, margin: '0 0 5px' }}>Turn {index + 1}</p>
      <p style={{ ...S.p, fontStyle: 'italic', margin: '0 0 6px', fontSize: 13 }}>{entry.query}</p>
      <p style={{ ...S.p, margin: 0, fontSize: 13, whiteSpace: 'pre-wrap' }}>{entry.findings}</p>
    </div>
  );
}

function AwaitingEntryRow({
  index, entry, rawText, onChangeRawText, onSubmit, submitting, error,
}: {
  index: number;
  entry: ResearchSession['entries'][number];
  rawText: string;
  onChangeRawText: (v: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string;
}) {
  const canSubmit = rawText.trim().length > 0 && !submitting;
  return (
    <div style={{
      border: `1px solid ${T.text}`, borderRadius: 5, padding: '12px 14px',
      marginBottom: 10, background: T.card,
    }}>
      <p style={{ ...S.h3, margin: '0 0 5px' }}>Turn {index + 1} — awaiting your findings</p>
      <p style={{ ...S.p, fontStyle: 'italic', margin: '0 0 8px', fontSize: 13 }}>{entry.query}</p>
      <label style={S.label}>Paste what you found *</label>
      <textarea
        value={rawText}
        onChange={e => onChangeRawText(e.target.value)}
        placeholder="Paste the case text, statute section, textbook passage, or search-result text you found for this query…"
        style={S.ta}
        disabled={submitting}
      />
      {error && <p style={{ fontSize: 12, color: T.err, margin: '8px 0 0' }}>{error}</p>}
      <button type="button" onClick={onSubmit} disabled={!canSubmit} style={canSubmit ? S.btn : S.btnOff}>
        {submitting ? 'Synthesizing…' : 'Submit Findings'}
      </button>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{
      marginBottom: 14, padding: '10px 14px', background: '#fff4f4',
      border: `1px solid ${T.err}`, borderRadius: 5,
    }}>
      <p style={{ ...S.p, color: T.err, margin: 0, fontSize: 12 }}>{message}</p>
    </div>
  );
}

type Stage = 'idle' | 'selecting' | 'querying' | 'submitting' | 'reporting' | 'drafting';

export function ArticleDashboard() {
  const { setView } = useAppStore();

  const [topicEntry, setTopicEntry] = useState<ArticleTopicLogEntry | null>(null);
  const [session, setSession] = useState<ResearchSession | null>(null);
  const [rawText, setRawText] = useState('');
  const [draft, setDraft] = useState<ArticleDraft | null>(null);

  const [stage, setStage] = useState<Stage>('idle');
  const [error, setError] = useState('');

  const busy = stage !== 'idle';
  const last = session?.entries[session.entries.length - 1];
  const awaitingCurrent = last?.status === 'awaiting_findings';
  const completeCount = session?.entries.filter(e => e.status === 'complete').length ?? 0;

  async function newTopic() {
    setStage('selecting');
    setError('');
    setDraft(null);
    setSession(null);
    setTopicEntry(null);
    setRawText('');

    const result = await startArticleResearch();
    if (!result.ok || !result.session || !result.topicEntry) {
      setStage('idle');
      setError(result.error || 'Topic selection failed.');
      return;
    }
    setTopicEntry(result.topicEntry);

    // Immediately generate turn 1's query so counsel isn't left on an empty session.
    setStage('querying');
    const queryResult = await generateNextQuery(result.session);
    setStage('idle');
    setSession(queryResult.session);
    if (!queryResult.ok) setError(queryResult.error || 'Supo could not generate the first query.');
  }

  async function continueResearch() {
    if (!session || session.status !== 'active' || busy || awaitingCurrent) return;
    setStage('querying');
    setError('');
    const result = await generateNextQuery(session);
    setStage('idle');
    setSession(result.session);
    if (!result.ok) setError(result.error || 'Supo could not generate the next query.');
  }

  async function submit() {
    if (!session || !rawText.trim() || busy) return;
    setStage('submitting');
    setError('');
    const result = await submitFindings(session, rawText);
    setStage('idle');
    setSession(result.session);
    if (result.ok) setRawText('');
    if (!result.ok) setError(result.error || 'Supo could not synthesize these findings.');
  }

  function stopHere() {
    if (!session || session.status !== 'active') return;
    setSession({ ...session, status: 'abandoned', updated_at: new Date().toISOString() });
  }

  async function makeReport() {
    if (!session || completeCount === 0 || busy) return;
    setStage('reporting');
    setError('');
    const result = await generateFinalReport(session);
    setStage('idle');
    setSession(result.session);
    if (!result.ok) setError(result.error || 'Could not generate the report.');
  }

  async function makeDraft() {
    if (!session || !topicEntry || !session.final_report || busy) return;
    setStage('drafting');
    setError('');
    const result = await generateArticleDraft(session, topicEntry);
    setStage('idle');
    if (!result.ok || !result.draft) {
      setError(result.error || 'Draft generation failed.');
      return;
    }
    setDraft(result.draft);
  }

  function startOver() {
    setTopicEntry(null);
    setSession(null);
    setDraft(null);
    setRawText('');
    setError('');
    setStage('idle');
  }

  return (
    <div style={{ animation: 'fadeUp .3s ease', maxWidth: 620, margin: '0 auto' }}>

      {/* Masthead — same pattern as ContractDashboard / ResearchDashboard */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <button
            onClick={() => setView('home')}
            style={{
              background: 'none', border: `1px solid ${T.bdr}`, color: T.dim,
              borderRadius: 3, padding: '6px 16px', fontSize: 12,
              fontFamily: "'Times New Roman', Times, serif", cursor: 'pointer',
            }}
          >
            ← Back
          </button>
          <p style={{
            fontSize: 9, color: T.mute, fontFamily: "'Times New Roman', Times, serif",
            letterSpacing: '.2em', textTransform: 'uppercase',
          }}>
            Smart Tools · Articles
          </p>
        </div>
        <div style={{ borderTop: `2px solid ${T.text}`, paddingTop: 12 }}>
          <h1 style={{
            fontSize: 30, color: T.text, fontFamily: "'Times New Roman', Times, serif",
            fontWeight: 700, fontStyle: 'italic', lineHeight: 1.15, marginBottom: 10,
          }}>
            Article Generator
          </h1>
          <p style={{
            fontSize: 14, color: T.sub, fontFamily: "'Times New Roman', Times, serif",
            lineHeight: 1.65, borderTop: `1px solid ${T.bdr}`, paddingTop: 10,
          }}>
            Picks a topic, researches it turn by turn with your own pasted-in findings, writes up an
            OSCOLA-footnoted report, then drafts a copy-ready article from it. Nothing publishes automatically.
          </p>
        </div>
      </div>

      {!topicEntry && (
        <div style={{
          background: T.card, border: `1px solid ${T.bdr}`, borderRadius: 6,
          padding: '20px 20px 22px', textAlign: 'center',
        }}>
          <p style={{ ...S.hint, marginBottom: 14 }}>
            No topic selected yet — Supo picks something specific, cross-area, and not recently covered.
          </p>
          <button
            type="button"
            onClick={newTopic}
            disabled={busy}
            style={busy ? S.btnOff : { ...S.btn, marginTop: 0 }}
          >
            {stage === 'selecting' ? 'Selecting a topic…' : stage === 'querying' ? 'Generating first query…' : 'New Topic'}
          </button>
          {error && <div style={{ marginTop: 14, textAlign: 'left' }}><ErrorBanner message={error} /></div>}
        </div>
      )}

      {topicEntry && session && (
        <>
          {/* Topic strip */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 14, padding: '10px 14px', background: T.card,
            border: `1px solid ${T.bdrL}`, borderRadius: 4, gap: 12,
          }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ ...S.hint, margin: '0 0 3px', textTransform: 'uppercase', letterSpacing: '.08em', fontSize: 10 }}>
                {topicEntry.area}
              </p>
              <p style={{
                fontSize: 13, color: T.text, fontFamily: "'Times New Roman', Times, serif",
                margin: 0, lineHeight: 1.5,
              }}>
                {topicEntry.topic}
              </p>
            </div>
            <StatusPill status={session.status} />
          </div>

          {!draft && !session.final_report && session.entries.filter(e => e.status === 'complete').map((entry, i) => (
            <CompletedEntryRow key={entry.id} index={i} entry={entry} />
          ))}

          {!draft && !session.final_report && awaitingCurrent && last && (
            <AwaitingEntryRow
              index={session.entries.length - 1}
              entry={last}
              rawText={rawText}
              onChangeRawText={setRawText}
              onSubmit={submit}
              submitting={stage === 'submitting'}
              error={error}
            />
          )}

          {error && !awaitingCurrent && <ErrorBanner message={error} />}

          {!draft && session.final_report && (
            <div style={{
              background: '#ffffff', border: `1px solid ${T.bdr}`, borderRadius: 6,
              padding: '20px 22px', marginBottom: 14,
            }}>
              <p style={{ ...S.h3, margin: '0 0 10px' }}>Research Report</p>
              <div style={{
                fontSize: 13, color: T.sub, fontFamily: "'Times New Roman', Times, serif",
                lineHeight: 1.8, whiteSpace: 'pre-wrap',
              }}>
                {session.final_report}
              </div>
            </div>
          )}

          {!draft && (
            <>
              {!session.final_report && !awaitingCurrent && session.status === 'active' && (
                <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <button
                    type="button"
                    onClick={continueResearch}
                    disabled={busy}
                    style={{ ...(busy ? S.btnOff : S.btn), marginTop: 0, flex: 2 }}
                  >
                    {stage === 'querying'
                      ? `Generating query ${session.entries.length + 1}…`
                      : session.entries.length === 0 ? 'Start Research' : 'Continue Research'}
                  </button>
                  <button
                    type="button"
                    onClick={stopHere}
                    disabled={busy}
                    style={{
                      flex: 1, background: 'none', border: `1px solid ${T.bdr}`, color: T.dim,
                      borderRadius: 4, padding: '11px 14px', fontSize: 12, cursor: busy ? 'not-allowed' : 'pointer',
                      fontFamily: "'Times New Roman', Times, serif",
                    }}
                  >
                    Stop Here
                  </button>
                </div>
              )}

              <p style={{ ...S.hint, textAlign: 'center', marginBottom: 10 }}>
                {completeCount} of {MAX_RESEARCH_TURNS} research turns complete
              </p>

              {!session.final_report && (
                <button
                  type="button"
                  onClick={makeReport}
                  disabled={busy || completeCount === 0}
                  style={busy || completeCount === 0 ? S.btnOff : { ...S.btn, marginTop: 0, background: T.dim }}
                >
                  {stage === 'reporting' ? 'Writing report…' : 'Generate Report'}
                </button>
              )}

              {session.final_report && (
                <button
                  type="button"
                  onClick={makeDraft}
                  disabled={busy}
                  style={busy ? S.btnOff : { ...S.btn, marginTop: 10, background: T.dim }}
                >
                  {stage === 'drafting' ? 'Writing draft…' : 'Generate Draft'}
                </button>
              )}
            </>
          )}

          {draft && (
            <div style={{
              background: '#ffffff', border: `1px solid ${T.bdr}`, borderRadius: 6,
              padding: '22px 24px',
            }}>
              <h2 style={{
                fontSize: 22, color: T.text, fontFamily: "'Times New Roman', Times, serif",
                fontWeight: 700, fontStyle: 'italic', lineHeight: 1.3, marginBottom: 14,
              }}>
                {draft.title}
              </h2>
              <div style={{
                fontSize: 14, color: T.sub, fontFamily: "'Times New Roman', Times, serif",
                lineHeight: 1.85, whiteSpace: 'pre-wrap',
              }}>
                {draft.body}
              </div>
            </div>
          )}

          {(session.status !== 'active' || draft) && (
            <button type="button" onClick={startOver} style={{ ...S.btn, background: T.dim }}>
              Start a New Article
            </button>
          )}
        </>
      )}

      <p style={{
        marginTop: 32, fontSize: 11, color: T.mute, textAlign: 'center',
        fontFamily: "'Times New Roman', Times, serif", lineHeight: 1.8,
      }}>
        AFS Legal OS · Article Generator — topic, research, report, draft — nothing leaves this screen automatically.
      </p>
    </div>
  );
}
